import React, { useEffect, useState, useMemo } from "react";
import { ScrollArea, useToast, Spinner } from "../../components/ui";
import { Header, UserPreview } from "../../components/modules";
import constant, { statuses } from "../../constants";
import { FaCircleCheck, FaXmark } from "react-icons/fa6";
import { FiCheck, FiX, FiArrowLeft, FiExternalLink, FiUser, FiMapPin, FiPhone, FiCreditCard, FiClock } from "react-icons/fi";
import Context from "../../Context";
import { useParams, useNavigate } from "react-router-dom";
import { RiStore2Line } from "react-icons/ri";

export const ProductOrderInfo = () => {
  const { toast } = useToast();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { callApi } = Context();

  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [note, setNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);

  useEffect(() => {
    getOrder();
  }, [orderId]);

  const getOrder = async () => {
    setLoading(true);
    const { status, data } = await callApi(
      "products-getTransaction",
      { orderId },
      { notShowLoading: true }
    );

    if (status && data) {
      setOrder(data);
      if (data?.userId) {
        getUser(data.userId);
      }
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "ไม่พบคำสั่งซื้อ",
      });
      navigate("/product/orders");
    }
    setLoading(false);
  };

  const getUser = async (userId) => {
    const { status, data } = await callApi(
      "users-getUserInfoAdmin",
      { userId },
      { notShowLoading: true }
    );
    if (status && data) {
      setUser(data);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    const isCancel = newStatus === statuses.CANCEL;
    const confirmMessage = isCancel
      ? `คุณต้องการ "ยกเลิก" คำสั่งซื้อ #${order?.shortId || order?.id?.slice(-5)} ใช่หรือไม่?\n(ระบบจะทำการคืนสต็อกสินค้าและคืนแต้มสะสมกลับให้ลูกค้าโดยอัตโนมัติ)`
      : `คุณต้องการ "อนุมัติ" คำสั่งซื้อ #${order?.shortId || order?.id?.slice(-5)} ใช่หรือไม่?`;

    if (!window.confirm(confirmMessage)) return;

    setActionLoading(true);
    const { status } = await callApi(
      "products-editStatus",
      {
        id: order?.id,
        status: newStatus,
      },
      { notShowLoading: true }
    );
    setActionLoading(false);

    if (status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: (
          <div className="flex gap-2 items-center">
            <FaCircleCheck /> ปรับสถานะคำสั่งซื้อสำเร็จ
          </div>
        ),
      });
      getOrder();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "ปรับสถานะคำสั่งซื้อไม่สำเร็จ",
      });
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;

    setNoteLoading(true);
    const { status } = await callApi(
      "products-adminAddNote",
      {
        id: order.id,
        note: note.trim(),
      },
      { notShowLoading: true }
    );
    setNoteLoading(false);

    if (status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: (
          <div className="flex gap-2 items-center">
            <FaCircleCheck /> บันทึกโน้ตสำเร็จ
          </div>
        ),
      });
      setNote("");
      getOrder();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "บันทึกโน้ตไม่สำเร็จ",
      });
    }
  };

  const productList = useMemo(() => {
    if (!order?.product) return [];
    return Array.isArray(order.product) ? order.product : [order.product];
  }, [order?.product]);

  const allNotes = useMemo(() => {
    if (!order) return [];
    const notes = [...(order.notes || []), ...(order.note || [])];
    return notes.sort((a, b) => (b?.time || 0) - (a?.time || 0));
  }, [order]);

  if (loading && !order) {
    return (
      <div className="h-fit w-full max-w-[1600px] mx-auto min-h-[500px] flex flex-col items-center justify-center gap-3">
        <Spinner className="w-8 h-8" color="border-navy" />
        <span className="text-sm font-semibold text-gray-500">กำลังโหลดรายละเอียดคำสั่งซื้อ...</span>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-16">
      {/* Back Button & Header */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => {
            navigate("/product/orders");
          }}
          className="p-2 text-gray-500 hover:text-navy hover:bg-white rounded-md transition-all cursor-pointer flex items-center gap-1.5 text-sm font-semibold"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>กลับหน้ารายการ</span>
        </button>
      </div>

      <Header
        header="Products"
        subHeader={`รายละเอียดคำสั่งซื้อ #${order.shortId || order.id?.slice(-5).toUpperCase()}`}
        icon={<RiStore2Line className="text-navy" />}
      />

      {/* Overview Top Bar */}
      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md bg-navy/10 text-navy flex items-center justify-center font-mono font-bold text-lg">
            #{order.shortId}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-800">
                คำสั่งซื้อ #{order.shortId}
              </h2>
              {order.isPoint && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-navy/10 text-navy border border-navy/20">
                  แลกคะแนนสะสม
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold text-gray-500 mt-0.5">
              <span className="flex items-center gap-1">
                <FiClock className="w-3.5 h-3.5" />
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }) + " น."
                  : "-"}
              </span>
              <span>•</span>
              <span>ผู้สั่ง: {order.receiver?.name || user?.name || "-"}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {order.status === statuses.APPROVE ? (
            <span className="px-3 py-1.5 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-1.5">
              <FiCheck className="w-4 h-4" />
              อนุมัติแล้ว
            </span>
          ) : order.status === statuses.PAID ? (
            <span className="px-3 py-1.5 text-sm font-bold text-navy bg-navy/10 border border-navy/20 rounded-md">
              ชำระเงินแล้ว (รอตรวจสอบ)
            </span>
          ) : order.status === statuses.CANCEL ? (
            <span className="px-3 py-1.5 text-sm font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-md flex items-center gap-1.5">
              <FiX className="w-4 h-4" />
              ยกเลิกคำสั่งซื้อ
            </span>
          ) : (
            <span className="px-3 py-1.5 text-sm font-bold text-gray-700 bg-gray-100 border border-gray-200 rounded-md">
              {order.status}
            </span>
          )}
        </div>
      </div>

      {/* Main Grid: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Left 2 Columns: Order Details & Customer */}
        <div className="lg:col-span-2 space-y-6">
          {/* Products List Card */}
          <div className="bg-white rounded-md shadow-md border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-between">
              <span>รายการสินค้า ({productList.length} รายการ)</span>
              {order.isPickup && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                  รับเองที่สำนักงานใหญ่วันดี
                </span>
              )}
            </h3>

            <div className="divide-y divide-gray-100 border-t border-b border-gray-100">
              {productList.map((prod, idx) => (
                <div key={idx} className="py-3.5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-md border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
                      {prod.image ? (
                        <img src={prod.image} alt={prod.name} className="w-full h-full object-cover" />
                      ) : (
                        <RiStore2Line className="w-6 h-6 text-gray-300" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-sm text-gray-800">{prod.name || "สินค้า"}</p>
                      {prod.shortId && (
                        <span className="text-xs text-gray-400 font-semibold">#{prod.shortId}</span>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-bold text-sm text-gray-800">
                      {order.isPoint
                        ? `${(prod.point || 0).toLocaleString()} pts`
                        : `฿${(Number(prod.price) || 0).toLocaleString()}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            <div className="mt-4 pt-2 space-y-2 text-sm font-semibold">
              <div className="flex items-center justify-between text-gray-600">
                <span>ราคาสินค้ารวม</span>
                <span>
                  {order.isPoint
                    ? `${(order.point || 0).toLocaleString()} pts`
                    : `฿${(Number(order.totalPrice || order.price) - (order.isPickup ? 0 : 40 + productList.length * 10) || 0).toLocaleString()}`}
                </span>
              </div>

              {!order.isPickup && (
                <>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>ค่าจัดส่งพัสดุ</span>
                    <span>฿40</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>ค่าบริการหีบห่อ (ชิ้นละ 10 บาท x {productList.length})</span>
                    <span>฿{(productList.length * 10).toLocaleString()}</span>
                  </div>
                </>
              )}

              {order.isPoint && (
                <>
                  <div className="flex items-center justify-between text-navy border-t border-gray-100 pt-2">
                    <span>คะแนนก่อนหัก</span>
                    <span>{(order.pointBeforeCharge || 0).toLocaleString()} pts</span>
                  </div>
                  <div className="flex items-center justify-between text-navy">
                    <span>คะแนนคงเหลือหลังหัก</span>
                    <span>{(order.pointAfterCharge || 0).toLocaleString()} pts</span>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between text-base font-bold pt-3 border-t border-gray-200 text-gray-900">
                <span>ยอดเงินที่ต้องชำระจริง</span>
                <span className="text-lg text-rose-600">
                  ฿{(Number(order.totalPrice ?? order.price) || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Customer Note */}
            {order.customerNote && (
              <div className="mt-4 p-3 bg-amber-50/70 border border-amber-200 rounded-md text-sm">
                <span className="font-bold text-amber-900">หมายเหตุจากลูกค้า: </span>
                <span className="text-amber-800">{order.customerNote}</span>
              </div>
            )}
          </div>

          {/* Shipping & Receiver Card */}
          <div className="bg-white rounded-md shadow-md border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FiMapPin className="text-navy w-5 h-5" />
              <span>ข้อมูลการจัดส่งและผู้รับ</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-semibold">
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-xs text-gray-400 block mb-1">ชื่อผู้รับ</span>
                <p className="text-gray-800 font-bold flex items-center gap-2">
                  <FiUser className="w-4 h-4 text-gray-400" />
                  {order.receiver?.name || "-"}
                </p>
              </div>

              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-xs text-gray-400 block mb-1">เบอร์โทรศัพท์</span>
                <p className="text-gray-800 font-bold flex items-center gap-2">
                  <FiPhone className="w-4 h-4 text-gray-400" />
                  {order.receiver?.phone || "-"}
                </p>
              </div>

              <div className="col-span-1 sm:col-span-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-xs text-gray-400 block mb-1">ที่อยู่จัดส่ง</span>
                <p className="text-gray-800 leading-relaxed">
                  {order.isPickup
                    ? "รับเองที่สำนักงานใหญ่วันดี"
                    : order.receiver?.address || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* User Account Details */}
          {user && <UserPreview user={user} />}
        </div>

        {/* Right 1 Column: Actions, Slip & Notes */}
        <div className="space-y-6">
          {/* Status & Actions Card */}
          <div className="bg-white rounded-md shadow-md border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              ดำเนินการคำสั่งซื้อ
            </h3>

            {order.status === statuses.PAID ? (
              <div className="space-y-3">
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => handleUpdateStatus(statuses.APPROVE)}
                  className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Spinner className="w-4 h-4" color="border-white" />
                  ) : (
                    <FiCheck className="w-4 h-4" />
                  )}
                  <span>อนุมัติคำสั่งซื้อ</span>
                </button>

                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => handleUpdateStatus(statuses.CANCEL)}
                  className="w-full py-2 px-4 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-md font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Spinner className="w-4 h-4" color="border-rose-600" />
                  ) : (
                    <FiX className="w-4 h-4" />
                  )}
                  <span>ยกเลิกคำสั่งซื้อ (คืนสต็อก/แต้ม)</span>
                </button>
              </div>
            ) : order.status === statuses.APPROVE ? (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md space-y-2 text-sm font-semibold text-emerald-900">
                  <div className="flex items-center gap-1.5 font-bold">
                    <FiCheck className="w-4 h-4 text-emerald-600" />
                    <span>คำสั่งซื้อนี้ได้รับการอนุมัติแล้ว</span>
                  </div>
                  {order.approvedBy && (
                    <p className="text-xs text-emerald-700">อนุมัติโดย: {order.approvedBy}</p>
                  )}
                  {order.approvedAt && (
                    <p className="text-xs text-emerald-700">
                      เวลา:{" "}
                      {new Date(order.approvedAt).toLocaleDateString("th-TH", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => handleUpdateStatus(statuses.CANCEL)}
                  className="w-full py-2 px-4 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-md font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Spinner className="w-4 h-4" color="border-rose-600" />
                  ) : (
                    <FiX className="w-4 h-4" />
                  )}
                  <span>ยกเลิกคำสั่งซื้อนี้ (คืนสต็อก/แต้ม)</span>
                </button>
              </div>
            ) : order.status === statuses.CANCEL ? (
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-md space-y-1 text-sm font-semibold text-rose-900">
                <p className="font-bold flex items-center gap-1.5">
                  <FiX className="w-4 h-4 text-rose-600" />
                  <span>คำสั่งซื้อนี้ถูกยกเลิกแล้ว</span>
                </p>
                {order.canceledBy && (
                  <p className="text-xs text-rose-700">ยกเลิกโดย: {order.canceledBy}</p>
                )}
                {order.canceledAt && (
                  <p className="text-xs text-rose-700">
                    เวลา:{" "}
                    {new Date(order.canceledAt).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm font-semibold text-gray-700">
                  สถานะ: {order.status}
                </div>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => handleUpdateStatus(statuses.CANCEL)}
                  className="w-full py-2 px-4 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-md font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Spinner className="w-4 h-4" color="border-rose-600" />
                  ) : (
                    <FiX className="w-4 h-4" />
                  )}
                  <span>ยกเลิกคำสั่งซื้อ (คืนสต็อก/แต้ม)</span>
                </button>
              </div>
            )}
          </div>

          {/* Slip Card */}
          <div className="bg-white rounded-md shadow-md border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-between">
              <span>สลิปโอนเงิน</span>
              {order.slipId && (
                <span className="text-xs font-mono font-semibold text-gray-500">
                  ID: {order.slipId.slice(0, 16)}...
                </span>
              )}
            </h3>

            {order.slip ? (
              <div className="space-y-3">
                <div
                  onClick={() => setSelectedSlip(order.slip)}
                  className="rounded-md border border-gray-200 overflow-hidden cursor-pointer group relative aspect-3/4 max-h-72 flex items-center justify-center bg-gray-50"
                >
                  <img
                    src={order.slip}
                    alt="Slip"
                    className="w-full h-full object-contain group-hover:scale-102 transition-transform duration-150"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-bold gap-1.5 transition-opacity">
                    <FiExternalLink className="w-4 h-4" />
                    <span>คลิกเพื่อดูรูปขยาย</span>
                  </div>
                </div>

                <a
                  href={order.slip}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-md transition-all flex items-center justify-center gap-1.5"
                >
                  <FiExternalLink className="w-3.5 h-3.5" />
                  <span>เปิดรูปสลิปในแท็บใหม่</span>
                </a>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-400 font-semibold text-sm bg-gray-50 rounded-md border border-dashed border-gray-200">
                ไม่มีภาพสลิปแนบมา
              </div>
            )}
          </div>

          {/* Admin Notes Timeline */}
          <div className="bg-white rounded-md shadow-md border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              บันทึกโน้ตของแอดมิน
            </h3>

            {/* Note Input */}
            <form onSubmit={handleAddNote} className="flex gap-2 mb-4">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="พิมพ์บันทึกโน้ต..."
                className="flex-1 h-10 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:border-navy transition-all"
              />
              <button
                type="submit"
                disabled={noteLoading || !note.trim()}
                className="py-2.5 px-4 bg-navy hover:bg-navy/90 text-white rounded-md font-bold text-sm shadow-sm transition-all cursor-pointer disabled:opacity-50 shrink-0"
              >
                {noteLoading ? (
                  <Spinner className="w-4 h-4" color="border-white" />
                ) : (
                  "เพิ่ม"
                )}
              </button>
            </form>

            {/* Notes List */}
            <ScrollArea className="h-72 pr-2">
              {allNotes.length === 0 ? (
                <div className="py-8 text-center text-gray-400 font-semibold text-sm">
                  ยังไม่มีบันทึกโน้ต
                </div>
              ) : (
                <div className="space-y-3">
                  {allNotes.map((n, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm space-y-1"
                    >
                      <div className="flex items-center justify-between text-xs font-bold text-navy">
                        <span>{n.by || "แอดมิน"}</span>
                        <span className="text-gray-400 font-semibold">
                          {n.time
                            ? new Date(n.time).toLocaleDateString("th-TH", {
                                year: "2-digit",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>
                      <p className="text-gray-800 font-semibold leading-relaxed break-words">
                        {n.note}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Slip Modal Lightbox */}
      {selectedSlip && (
        <div
          onClick={() => setSelectedSlip(null)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4 cursor-pointer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-md overflow-hidden max-w-lg w-full max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-150"
          >
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <span className="text-sm font-bold text-gray-800">สลิปการโอนเงิน</span>
              <button
                onClick={() => setSelectedSlip(null)}
                className="p-1 text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                <FaXmark className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center overflow-auto max-h-[80vh]">
              <img src={selectedSlip} alt="Slip Full" className="max-w-full max-h-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
